'use client'

import { Check, ChevronsUpDown } from 'lucide-react'
import { type ReactNode, forwardRef, useState } from 'react'

import { Button } from '@/components/button/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/command/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'

import { cn } from '@/lib/utils'

export interface ComboboxProps {
  items: ComboboxItem[]
  value: ComboboxItem['value']
  disabled?: boolean
  placeholder?: ReactNode
  searchPlaceholder?: string
  emptyPlaceholder?: ReactNode
  /**
   * If it is in a modal
   */
  modal?: boolean
  className?: {
    triggerButton?: string
  }
  onValueChange: (selectedItem: ComboboxItem['value']) => void
}

export interface ComboboxItem {
  label: string
  value: string | undefined
}

export const Combobox = forwardRef<HTMLButtonElement, ComboboxProps>(
  (
    {
      items,
      value,
      disabled,
      placeholder = 'Please select...',
      searchPlaceholder,
      emptyPlaceholder,
      modal,
      className,
      onValueChange,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false)

    function handleSelectItem(selectedLabel: ComboboxItem['value']) {
      const targetValue = items.find((item) => item.label === selectedLabel)?.value || ''
      onValueChange(targetValue)
      setOpen(false)
    }

    return (
      <Popover open={open} modal={modal} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className={cn('w-full justify-between', 'disabled:cursor-not-allowed', className?.triggerButton)}
          >
            <span className={cn('flex-1', 'overflow-x-hidden overflow-ellipsis', 'text-left')}>
              {value ? items.find((item) => item.value === value)?.label || '' : placeholder}
            </span>
            <span className={cn('shrink-0')}>
              <ChevronsUpDown className={cn('ml-2', 'h-4', 'w-4', 'opacity-50')} />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 popover-content-width-same-as-its-trigger"
          onWheel={($event) => {
            // Workaround for the bug(https://github.com/shadcn-ui/ui/issues/607).
            $event.stopPropagation()
          }}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder ?? 'Search'} />
            <CommandList>
              <CommandEmpty>{emptyPlaceholder ?? 'No items'}</CommandEmpty>
              <CommandGroup>
                {items.map((item) => (
                  <CommandItem key={item.value} onSelect={handleSelectItem}>
                    <Check
                      className={cn(
                        'mr-2 max-h-4 min-h-4 min-w-4 max-w-4',
                        item.value === value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {item.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  }
)
Combobox.displayName = 'Combobox'
